'use client';

import mediaText from "@/styles/modules/media_content.module.scss";
import * as C from "@components";
import { useReveal } from "@/components/Reveal/useReveal";
import reveal from "@/components/Reveal/reveal.module.scss";
import React, { useRef, useState, useMemo, memo } from "react";
import { PageLink, PageLinkVariant } from '@components';
import * as Api from '@api';
import * as P from "@parts";

const enum PlayingState
{
    Initial,
    Playing,
    Paused,
    Played,
}

export type ContentVideoBlockProps = {
    pageContent?: Api.Content<typeof P.ProductAutomationSectionKeys[number]>;
    content?: string | null;
    videoId?: string;
    videoUrl?: string;
    videoNode?: React.ReactNode;
    videoWidth: number;
    videoHeight: number;
    videoPosition: 'left' | 'right';
    columnsClassName?: string;
    className?: string;
    imagePlaceholder?: string;
    loop?: boolean;
}

type MemoizedMotionDivProps = {
    className: string;
    refProp: any;
    children: React.ReactNode;
}

// Was framer-motion's m.div; now a plain div whose reveal classes are passed in via className.
// eslint-disable-next-line react/display-name
const MemoizedMotionDiv = memo(({ className, refProp, children }: MemoizedMotionDivProps) => (
    <div
        className={className}
        ref={refProp}
    >
        {children}
    </div>
));

export function ContentVideoBlock(props: ContentVideoBlockProps)
{
    const { pageContent, content, videoId, videoUrl, videoWidth, videoHeight, videoPosition, videoNode, columnsClassName, className, imagePlaceholder, loop } = props;

    // Was framer-motion (opacity + translateX, 1.5s). Now CSS reveal: left box slides from the left,
    // right box from the right — identical to the old variants.
    const left = useReveal<HTMLDivElement>();
    const right = useReveal<HTMLDivElement>();

    const padding = videoWidth * 100 / videoHeight;

    const videoRef = useRef<HTMLVideoElement>(null);
    const [playingState, setPlayingState] = useState<PlayingState>(PlayingState.Playing);

    const imageContent = useMemo(() => (
        <div>
            {videoId &&
                <div className="responsive-video" style={{ paddingBottom: padding + '%' }}>
                    <iframe src={`https://player.vimeo.com/video/${videoId}?h=4ffe41bd67&autoplay=1&loop=1&color=e87201&title=0&byline=0&portrait=0&muted=1&background=1`}
                        frameBorder="0"
                        allow="autoplay"
                        allowFullScreen></iframe>
                </div>
            }
            {videoUrl &&
                <div className="responsive-video" style={{ paddingBottom: padding + '%' }}>
                    <C.InplaceVideo
                        loop={loop ?? false}
                        ref={videoRef} muted autoPlay
                        onPlaying={() => setPlayingState(PlayingState.Playing)} onPause={() => setPlayingState(PlayingState.Paused)} onEnded={() => setPlayingState(PlayingState.Played)}
                        imagePlaceholder={imagePlaceholder} imagePlaceholderAlt={videoUrl} imagePlaceholderWidth={videoWidth} imagePlaceholderHeight={videoHeight}
                    >
                        <source src={videoUrl} type="video/mp4" />
                    </C.InplaceVideo>
                    {
                        !loop && (playingState === PlayingState.Initial || playingState === PlayingState.Paused) &&
                        <button className={"play"} type={"button"} onClick={() => videoRef.current?.play()}>
                            <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="30" cy="30" r="30" fill="#fff" />
                                <path d="M27.0765 38.0765L27.0765 30.1005L27.0765 22.1247L38.8995 30.1005L27.0765 38.0765Z" fill="#414042" />
                            </svg>
                        </button>
                    }
                    {
                        !loop && playingState === PlayingState.Playing &&
                        <button className={"pause"} type={"button"} onClick={() => videoRef.current?.pause()}>
                            <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="30" cy="30" r="30" fill="#fff" />
                                <rect x="32" y="23" width="4" height="14" fill="#414042" />
                                <rect x="24" y="23" width="4" height="14" fill="#414042" />
                            </svg>
                        </button>
                    }
                    {
                        !loop && playingState === PlayingState.Played &&
                        <button className={"play"} type={"button"} onClick={() => videoRef.current?.play()}>
                            <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="30" cy="30" r="30" fill="#fff" />
                                <path d="M29.062 37.885c3.644 0 6.718-2.485 7.618-5.849h2.144L35.542 26.353 32.262 32.036h1.884c-.809 2.015-2.783 3.442-5.084 3.442-3.02 0-5.478-2.457-5.478-5.478 0-3.02 2.457-5.478 5.478-5.478 1.554 0 2.957.651 3.955 1.693l1.237-2.143c-1.388-1.218-3.204-1.958-5.192-1.958C24.714 22.115 21.177 25.652 21.177 30c0 4.348 3.536 7.885 7.885 7.885z" fill="#414042" />
                            </svg>
                        </button>
                    }
                </div>
            }
            {videoNode}
        </div>
    ), [videoId, padding, videoUrl, loop, imagePlaceholder, videoWidth, videoHeight, playingState, videoNode]);

    const textContent = useMemo(() => (
        pageContent ? <>
            {pageContent["automation.headline"]?.data && <C.PageContent {...pageContent["automation.headline"]}  layout={{ all: { margins: { bottom: C.Margin.D20 } }, desktop: { margins: { bottom: C.Margin.D40 } } }} typography={C.Typography.H3} className="max-md:!text-[32px]" />}
            {pageContent["automation.body"]?.data && <C.PageContent {...pageContent["automation.body"]} layout={{ all: { margins: { bottom: C.Margin.D20 } }, desktop: { margins: { bottom: C.Margin.D40 }, maxWidth: C.Width.W414 } }} typography={C.Typography.P2} />}
            {pageContent["automation.link"]?.data && <div className="flex max-md:justify-center">
                <PageLink href="/automated-filling-and-capping" variant={PageLinkVariant.Link}>{pageContent["automation.link"].data}</PageLink>
            </div>}
        </> : <><div dangerouslySetInnerHTML={{ __html: content ?? "" }}></div></>
    ), [pageContent, content]);

    return (<section className={`${mediaText.content_media__wrapper} overflow-hidden`}>
        <div className={`container ${className}`}>
            <div className={`flex flex-col md:flex-row md:flex-nowrap justify-between md:items-center ${columnsClassName}`}>
                <MemoizedMotionDiv
                    className={`${videoPosition === 'left' ? 'box image' : 'box text'} ${reveal.reveal} ${reveal.fromLeft} ${left.shown ? reveal.shown : ''}`}
                    refProp={left.ref}
                >
                    {videoPosition === 'left' ? imageContent : textContent}
                </MemoizedMotionDiv>
                <MemoizedMotionDiv
                    className={`${videoPosition === 'right' ? 'box image' : 'box text'} ${reveal.reveal} ${reveal.fromRight} ${right.shown ? reveal.shown : ''}`}
                    refProp={right.ref}
                >
                    {videoPosition === 'right' ? imageContent : textContent}
                </MemoizedMotionDiv>
            </div>
        </div>
    </section>
    );
}

export default ContentVideoBlock;